import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { RiDatabase2Line, RiDownloadLine, RiFileCopyLine } from '@remixicon/react';
import {
  Button,
  CopyButton,
  ExternalLink,
  Flex,
  InfraCodeBlock,
  InfraDivider,
  InfraField,
  InfraSection,
  InfraStack,
  InfraTag,
  InfraTagRow,
  Text,
} from '../ui/InfraPrimitives';

export const CRDBInfoCard = () => {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations || {};

  const provider = annotations['infrastructure.backstage.io/provider'] || '';
  const isAWS = provider === 'aws';
  const isGCP = provider === 'gcp';

  const cluster1UIUrl = annotations['redis-crdb.infrastructure/cluster1-ui-url'];
  const cluster2UIUrl = annotations['redis-crdb.infrastructure/cluster2-ui-url'];
  const cluster1FQDN = annotations['redis-crdb.infrastructure/cluster1-fqdn'];
  const cluster2FQDN = annotations['redis-crdb.infrastructure/cluster2-fqdn'];
  const cluster1NodeCount = annotations['redis-crdb.infrastructure/cluster1-node-count'];
  const cluster2NodeCount = annotations['redis-crdb.infrastructure/cluster2-node-count'];
  const cluster1PublicIPs = annotations['redis-crdb.infrastructure/cluster1-node-public-ips'];
  const cluster2PublicIPs = annotations['redis-crdb.infrastructure/cluster2-node-public-ips'];
  const cluster1PrivateIPs = annotations['redis-crdb.infrastructure/cluster1-node-ips'];
  const cluster2PrivateIPs = annotations['redis-crdb.infrastructure/cluster2-node-ips'];
  const redisVersion = annotations['redis-crdb.infrastructure/version'];
  const instanceType = annotations['redis-crdb.infrastructure/instance-type'];

  const awsRegion = annotations['aws.infrastructure/region'];
  const gcpRegion = annotations['gcp.infrastructure/region'];
  const gcpZone = annotations['gcp.infrastructure/zone'];
  const region = awsRegion || gcpRegion;

  const awsVpcId = annotations['aws.infrastructure/vpc-id'];
  const gcpNetwork = annotations['gcp.infrastructure/network'];
  const vpcName = annotations['redis-crdb.infrastructure/vpc-name'];
  const vpcId = awsVpcId || gcpNetwork || vpcName;

  const gcpProjectId = annotations['gcp.infrastructure/project-id'];
  const cluster1ConsoleUrls = annotations['redis-crdb.infrastructure/cluster1-console-urls'];
  const cluster2ConsoleUrls = annotations['redis-crdb.infrastructure/cluster2-console-urls'];
  const cluster1GcloudSshCommands = annotations['redis-crdb.infrastructure/cluster1-gcloud-ssh-commands'];
  const cluster2GcloudSshCommands = annotations['redis-crdb.infrastructure/cluster2-gcloud-ssh-commands'];
  const cluster1SshCommands = annotations['redis-crdb.infrastructure/cluster1-ssh-commands'];
  const cluster2SshCommands = annotations['redis-crdb.infrastructure/cluster2-ssh-commands'];

  const sshPrivateKeyEncoded = annotations['redis-enterprise.infrastructure/ssh-private-key'];

  const hasCRDBInfo = cluster1UIUrl || cluster1FQDN || cluster2FQDN;

  if (!hasCRDBInfo) {
    return null;
  }

  const cluster1AdminUser = cluster1FQDN ? `admin@${cluster1FQDN}` : 'admin@cluster1.redis.local';
  const cluster2AdminUser = cluster2FQDN ? `admin@${cluster2FQDN}` : 'admin@cluster2.redis.local';

  const cluster1PublicIPsList = cluster1PublicIPs?.split(',') || [];
  const cluster2PublicIPsList = cluster2PublicIPs?.split(',') || [];
  const cluster1PrivateIPsList = cluster1PrivateIPs?.split(',') || [];
  const cluster2PrivateIPsList = cluster2PrivateIPs?.split(',') || [];

  const sshPrivateKey = sshPrivateKeyEncoded ? atob(sshPrivateKeyEncoded) : '';

  const sshUser = isGCP ? 'cloud-user' : 'ec2-user';

  const cluster1MasterIP = cluster1PublicIPsList[0] || cluster1PrivateIPsList[0] || '<cluster1-master-ip>';
  const passwordRetrievalCmd = `ssh -i ansible_key.pem ${sshUser}@${cluster1MasterIP} sudo cat /opt/redislabs/.cluster_admin_password`;

  const cluster1GcloudSshCommandsList = cluster1GcloudSshCommands?.split('|||') || [];
  const cluster2GcloudSshCommandsList = cluster2GcloudSshCommands?.split('|||') || [];
  const cluster1ConsoleUrlsList = cluster1ConsoleUrls?.split('|||') || [];
  const cluster2ConsoleUrlsList = cluster2ConsoleUrls?.split('|||') || [];
  const cluster1SshCommandsList = cluster1SshCommands?.split('|||') || [];
  const cluster2SshCommandsList = cluster2SshCommands?.split('|||') || [];

  const sshCommands: string[] =
    cluster1SshCommandsList.length > 0 || cluster2SshCommandsList.length > 0
      ? [...cluster1SshCommandsList, ...cluster2SshCommandsList]
      : (() => {
          const cmds: string[] = [];
          cluster1PublicIPsList.forEach((ip, index) => {
            cmds.push(`ssh -i ansible_key.pem ${sshUser}@${ip}  # Cluster 1 Node ${index + 1}`);
          });
          cluster2PublicIPsList.forEach((ip, index) => {
            cmds.push(`ssh -i ansible_key.pem ${sshUser}@${ip}  # Cluster 2 Node ${index + 1}`);
          });
          return cmds;
        })();

  const downloadSSHKey = () => {
    const element = document.createElement('a');
    const file = new Blob([sshPrivateKey], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = 'ansible_key.pem';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const crdbCreateSnippet = `# Replace <paste-password-here> with the shared admin password retrieved above.
curl -k -u ${cluster1AdminUser}:<paste-password-here> \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "demo-crdb",
    "port": 12000,
    "encryption": false,
    "default_db_config": {
      "memory_size": 2147483648,
      "port": 12000,
      "shards_count": 1,
      "replication": true
    },
    "instances": [
      {
        "cluster": {
          "name": "${cluster1FQDN || 'cluster1.redis.local'}",
          "url": "https://${cluster1FQDN || 'cluster1.redis.local'}:9443",
          "credentials": {
            "username": "${cluster1AdminUser}",
            "password": "<paste-password-here>"
          },
          "replication_endpoint": "redis-12000.${cluster1FQDN || 'cluster1.redis.local'}:12000"
        }
      },
      {
        "cluster": {
          "name": "${cluster2FQDN || 'cluster2.redis.local'}",
          "url": "https://${cluster2FQDN || 'cluster2.redis.local'}:9443",
          "credentials": {
            "username": "${cluster2AdminUser}",
            "password": "<paste-password-here>"
          },
          "replication_endpoint": "redis-12000.${cluster2FQDN || 'cluster2.redis.local'}:12000"
        }
      }
    ]
  }' \\
  https://${cluster1FQDN || 'cluster1.redis.local'}:9443/v1/crdbs`;

  const redisCliSnippet = `# Connect to Cluster 1
redis-cli -h ${cluster1FQDN ? `redis-12000.${cluster1FQDN}` : '<cluster1-endpoint>'} -p 12000

# Connect to Cluster 2
redis-cli -h ${cluster2FQDN ? `redis-12000.${cluster2FQDN}` : '<cluster2-endpoint>'} -p 12000`;

  return (
    <InfoCard
      title="Redis Enterprise CRDB (Active-Active) Information"
      icon={<RiDatabase2Line size={20} />}
    >
      <InfraStack>
        <InfraSection title="☁️ Global Configuration">
          {redisVersion && (
            <InfraField
              label="Redis Version:"
              value={
                <InfraTagRow>
                  <InfraTag>{redisVersion}</InfraTag>
                </InfraTagRow>
              }
              mono={false}
            />
          )}
          {instanceType && (
            <InfraField
              label={`${isGCP ? 'Machine Type' : 'Instance Type'}:`}
              value={
                <InfraTagRow>
                  <InfraTag>{instanceType}</InfraTag>
                </InfraTagRow>
              }
              mono={false}
            />
          )}
          {region && (
            <InfraField
              label={`${isGCP ? 'GCP Region' : 'AWS Region'}:`}
              value={
                <InfraTagRow>
                  <InfraTag>{region}</InfraTag>
                </InfraTagRow>
              }
              mono={false}
            />
          )}
          {isGCP && gcpZone && (
            <InfraField
              label="GCP Zone:"
              value={
                <InfraTagRow>
                  <InfraTag>{gcpZone}</InfraTag>
                </InfraTagRow>
              }
              mono={false}
            />
          )}
          {vpcId && (
            <InfraField label={`${isGCP ? 'VPC Network' : 'VPC ID'}:`} value={vpcId} />
          )}
          {isGCP && gcpProjectId && (
            <InfraField label="GCP Project:" value={gcpProjectId} />
          )}
        </InfraSection>

        <InfraSection title="🔵 Cluster 1">
          {cluster1FQDN && <InfraField label="DNS Zone:" value={cluster1FQDN} />}
          {cluster1NodeCount && (
            <InfraField
              label="Node Count:"
              value={
                <InfraTagRow>
                  <InfraTag>{cluster1NodeCount}</InfraTag>
                </InfraTagRow>
              }
              mono={false}
            />
          )}
          {cluster1UIUrl && (
            <InfraField
              label="Web UI:"
              value={<ExternalLink href={cluster1UIUrl}>{cluster1UIUrl}</ExternalLink>}
              mono={false}
            />
          )}
          {cluster1PublicIPsList.length > 0 && (
            <InfraField label="Public IPs:" value={cluster1PublicIPsList.join(', ')} />
          )}
          {cluster1PrivateIPsList.length > 0 && (
            <InfraField label="Private IPs:" value={cluster1PrivateIPsList.join(', ')} />
          )}
        </InfraSection>

        <InfraSection title="🟢 Cluster 2">
          {cluster2FQDN && <InfraField label="DNS Zone:" value={cluster2FQDN} />}
          {cluster2NodeCount && (
            <InfraField
              label="Node Count:"
              value={
                <InfraTagRow>
                  <InfraTag>{cluster2NodeCount}</InfraTag>
                </InfraTagRow>
              }
              mono={false}
            />
          )}
          {cluster2UIUrl && (
            <InfraField
              label="Web UI:"
              value={<ExternalLink href={cluster2UIUrl}>{cluster2UIUrl}</ExternalLink>}
              mono={false}
            />
          )}
          {cluster2PublicIPsList.length > 0 && (
            <InfraField label="Public IPs:" value={cluster2PublicIPsList.join(', ')} />
          )}
          {cluster2PrivateIPsList.length > 0 && (
            <InfraField label="Private IPs:" value={cluster2PrivateIPsList.join(', ')} />
          )}
        </InfraSection>

        <InfraDivider />
        <InfraSection title="🔑 Cluster Admin Credentials">
          <Text variant="title-small">Cluster 1</Text>
          <InfraField label="Username:" value={cluster1AdminUser} />
          <InfraField label="Password:" value="shared with Cluster 2 (retrieve below)" mono={false} />
          <Text variant="body-small" style={{ display: 'block', marginBottom: '0.75rem' }}>
            ⚠️ Use the full username (including @domain) when logging into the Web UI
          </Text>

          <Text variant="title-small">Cluster 2</Text>
          <InfraField label="Username:" value={cluster2AdminUser} />
          <InfraField label="Password:" value="shared with Cluster 1 (retrieve below)" mono={false} />
          <Text variant="body-small" style={{ display: 'block', marginBottom: '0.75rem' }}>
            ⚠️ Use the full username (including @domain) when logging into the Web UI
          </Text>

          <Text variant="title-small">Retrieve shared admin password:</Text>
          <div style={{ position: 'relative' }}>
            <InfraCodeBlock>{passwordRetrievalCmd}</InfraCodeBlock>
            <div style={{ position: 'absolute', top: 4, right: 4 }}>
              <CopyButton text={passwordRetrievalCmd} />
            </div>
          </div>

          <Text variant="body-small" style={{ display: 'block', marginTop: '0.75rem' }}>
            💡 <strong>Tip:</strong> Both clusters share the same admin password (required by the CRDB REST API).
            The password is generated at provisioning time and stored at
            <code> /opt/redislabs/.cluster_admin_password</code> on each cluster&apos;s master node.
            CRDB database creation is disabled by default — create CRDB via UI or REST API.
          </Text>
        </InfraSection>

        {sshPrivateKey && (
          <>
            <InfraDivider />
            <InfraSection title="🔐 SSH Access">
              <Text variant="title-small">SSH Private Key:</Text>
              <Flex gap="2" style={{ marginBottom: '0.75rem' }}>
                <Button variant="primary" size="small" onPress={downloadSSHKey}>
                  <RiDownloadLine size={16} />
                  Download Key
                </Button>
                <Button
                  variant="secondary"
                  size="small"
                  onPress={() => navigator.clipboard.writeText(sshPrivateKey)}
                >
                  <RiFileCopyLine size={16} />
                  Copy Key
                </Button>
              </Flex>

              {sshCommands.length > 0 && (
                <>
                  <Text variant="title-small">SSH Commands:</Text>
                  {sshCommands.map((cmd, index) => (
                    <div key={index} style={{ position: 'relative', marginBottom: '0.5rem' }}>
                      <InfraCodeBlock>{cmd}</InfraCodeBlock>
                      <div style={{ position: 'absolute', top: 4, right: 4 }}>
                        <CopyButton text={cmd} />
                      </div>
                    </div>
                  ))}
                </>
              )}

              <Text variant="body-small" style={{ display: 'block', marginTop: '0.75rem' }}>
                💡 <strong>Note:</strong> After downloading, set permissions: <code>chmod 600 ansible_key.pem</code>
              </Text>
            </InfraSection>
          </>
        )}

        {isGCP && (cluster1ConsoleUrlsList.length > 0 || cluster1GcloudSshCommandsList.length > 0) && (
          <>
            <InfraDivider />
            <InfraSection title="☁️ GCP Console & gcloud Access">
              {cluster1ConsoleUrlsList.length > 0 && (
                <>
                  <Text variant="title-small">Cluster 1 - GCP Console URLs:</Text>
                  {cluster1ConsoleUrlsList.map((url, index) => (
                    <div key={`c1-console-${index}`} style={{ marginBottom: '0.5rem' }}>
                      <ExternalLink href={url}>Node {index + 1} Console</ExternalLink>
                    </div>
                  ))}
                </>
              )}

              {cluster2ConsoleUrlsList.length > 0 && (
                <>
                  <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
                    Cluster 2 - GCP Console URLs:
                  </Text>
                  {cluster2ConsoleUrlsList.map((url, index) => (
                    <div key={`c2-console-${index}`} style={{ marginBottom: '0.5rem' }}>
                      <ExternalLink href={url}>Node {index + 1} Console</ExternalLink>
                    </div>
                  ))}
                </>
              )}

              {cluster1GcloudSshCommandsList.length > 0 && (
                <>
                  <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
                    Cluster 1 - gcloud SSH Commands:
                  </Text>
                  {cluster1GcloudSshCommandsList.map((cmd, index) => (
                    <div key={`c1-gcloud-${index}`} style={{ position: 'relative', marginBottom: '0.5rem' }}>
                      <InfraCodeBlock>{cmd}</InfraCodeBlock>
                      <div style={{ position: 'absolute', top: 4, right: 4 }}>
                        <CopyButton text={cmd} />
                      </div>
                    </div>
                  ))}
                </>
              )}

              {cluster2GcloudSshCommandsList.length > 0 && (
                <>
                  <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
                    Cluster 2 - gcloud SSH Commands:
                  </Text>
                  {cluster2GcloudSshCommandsList.map((cmd, index) => (
                    <div key={`c2-gcloud-${index}`} style={{ position: 'relative', marginBottom: '0.5rem' }}>
                      <InfraCodeBlock>{cmd}</InfraCodeBlock>
                      <div style={{ position: 'absolute', top: 4, right: 4 }}>
                        <CopyButton text={cmd} />
                      </div>
                    </div>
                  ))}
                </>
              )}

              <Text variant="body-small" style={{ display: 'block', marginTop: '0.75rem' }}>
                💡 <strong>Note:</strong> gcloud commands require the Google Cloud SDK to be installed and authenticated.
              </Text>
            </InfraSection>
          </>
        )}

        {isAWS && (cluster1ConsoleUrlsList.length > 0 || cluster1SshCommandsList.length > 0) && (
          <>
            <InfraDivider />
            <InfraSection title="☁️ AWS Console & SSH Access">
              {cluster1ConsoleUrlsList.length > 0 && (
                <>
                  <Text variant="title-small">Cluster 1 - AWS Console URLs:</Text>
                  {cluster1ConsoleUrlsList.map((url, index) => (
                    <div key={`c1-aws-console-${index}`} style={{ marginBottom: '0.5rem' }}>
                      <ExternalLink href={url}>Node {index + 1} Console →</ExternalLink>
                    </div>
                  ))}
                </>
              )}

              {cluster2ConsoleUrlsList.length > 0 && isAWS && (
                <>
                  <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
                    Cluster 2 - AWS Console URLs:
                  </Text>
                  {cluster2ConsoleUrlsList.map((url, index) => (
                    <div key={`c2-aws-console-${index}`} style={{ marginBottom: '0.5rem' }}>
                      <ExternalLink href={url}>Node {index + 1} Console →</ExternalLink>
                    </div>
                  ))}
                </>
              )}

              {cluster1SshCommandsList.length > 0 && (
                <>
                  <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
                    Cluster 1 - SSH Commands:
                  </Text>
                  {cluster1SshCommandsList.map((cmd, index) => (
                    <div key={`c1-ssh-${index}`} style={{ position: 'relative', marginBottom: '0.5rem' }}>
                      <InfraCodeBlock>{cmd}</InfraCodeBlock>
                      <div style={{ position: 'absolute', top: 4, right: 4 }}>
                        <CopyButton text={cmd} />
                      </div>
                    </div>
                  ))}
                </>
              )}

              {cluster2SshCommandsList.length > 0 && (
                <>
                  <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
                    Cluster 2 - SSH Commands:
                  </Text>
                  {cluster2SshCommandsList.map((cmd, index) => (
                    <div key={`c2-ssh-${index}`} style={{ position: 'relative', marginBottom: '0.5rem' }}>
                      <InfraCodeBlock>{cmd}</InfraCodeBlock>
                      <div style={{ position: 'absolute', top: 4, right: 4 }}>
                        <CopyButton text={cmd} />
                      </div>
                    </div>
                  ))}
                </>
              )}

              <Text variant="body-small" style={{ display: 'block', marginTop: '0.75rem' }}>
                💡 <strong>Note:</strong> SSH commands require the private key file (ansible_key.pem) to be saved locally.
              </Text>
            </InfraSection>
          </>
        )}

        <InfraDivider />
        <InfraSection title="🚀 Quick Start - Create CRDB Database">
          <Text variant="title-small">1. Access Cluster 1 Web UI:</Text>
          <InfraCodeBlock>{cluster1UIUrl || 'https://<cluster1-node-ip>:8443'}</InfraCodeBlock>

          <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
            2. Create CRDB via REST API (from Cluster 1):
          </Text>
          <InfraCodeBlock>{crdbCreateSnippet}</InfraCodeBlock>

          <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
            3. Connect to your CRDB database:
          </Text>
          <InfraCodeBlock>{redisCliSnippet}</InfraCodeBlock>

          <Text variant="body-small" style={{ display: 'block', marginTop: '0.75rem' }}>
            📚 <strong>Learn more:</strong>{' '}
            <ExternalLink href="https://redis.io/docs/latest/operate/rs/databases/active-active/">
              Redis Enterprise Active-Active Documentation
            </ExternalLink>
          </Text>
        </InfraSection>
      </InfraStack>
    </InfoCard>
  );
};
